import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldAlert, MailCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level dedup — a single-use magic-link token must NOT be re-submitted,
// but React 18 StrictMode double-invokes effects (mount → unmount → remount)
// which resets `useRef`. Keeping this map outside the component preserves it
// across the fake unmount/remount cycle. Values: 'in-flight' | 'succeeded' | 'failed'.
const _mlinkStatus = new Map();

/**
 * iter148 — Restaurant-owner magic-link consumer.
 *
 * URL: `/restaurant/auth/magic?token=<raw-token>`
 * Flow: read token → POST /auth/magic-link/verify → loginWithToken → /dashboard.
 * Single-use: retrying an already-used link shows a helpful error and a link
 * back to the login page to request a fresh sign-in link.
 */
const RestaurantMagicLink = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [state, setState] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const rawToken = (searchParams.get('token') || '').trim();
    if (!rawToken) {
      setState('error');
      setErrorMsg('This sign-in link is missing its token. Request a new one below.');
      return;
    }
    // If StrictMode already fired verify for this token, honour the prior result.
    const prior = _mlinkStatus.get(rawToken);
    if (prior === 'in-flight' || prior === 'succeeded') return;
    _mlinkStatus.set(rawToken, 'in-flight');
    (async () => {
      try {
        const res = await axios.post(`${API}/auth/magic-link/verify`, { token: rawToken });
        await loginWithToken(res.data.token);
        _mlinkStatus.set(rawToken, 'succeeded');
        setState('success');
        setTimeout(() => navigate('/dashboard', { replace: true }), 800);
      } catch (err) {
        _mlinkStatus.set(rawToken, 'failed');
        const detail = err?.response?.data?.detail;
        setState('error');
        setErrorMsg(
          detail === 'Sign-in link has expired. Request a new one.'
            ? 'This sign-in link has expired. Request a fresh one from the login page.'
            : detail === 'Invalid or already-used link'
              ? 'This sign-in link is invalid or has already been used. Request a new one.'
              : 'We could not verify your sign-in link. Please request a new one.',
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-zinc-900/70 border border-white/10 rounded-2xl p-8 text-center">
        {state === 'verifying' && (
          <div data-testid="rest-magic-verifying">
            <Loader2 className="w-10 h-10 text-yellow-400 mx-auto animate-spin" strokeWidth={1.75} />
            <h1 className="text-white text-xl font-bold tracking-tight mt-5">Signing you in…</h1>
            <p className="text-white/60 text-sm mt-2">Verifying your one-click sign-in link.</p>
          </div>
        )}
        {state === 'success' && (
          <div data-testid="rest-magic-success">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-500/[0.10] border border-emerald-500/30 flex items-center justify-center">
              <MailCheck className="w-7 h-7 text-emerald-300" strokeWidth={1.75} />
            </div>
            <h1 className="text-white text-xl font-bold tracking-tight mt-5">You're signed in</h1>
            <p className="text-white/60 text-sm mt-2">Redirecting to your dashboard…</p>
          </div>
        )}
        {state === 'error' && (
          <div data-testid="rest-magic-error">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-red-500/[0.10] border border-red-500/30 flex items-center justify-center">
              <ShieldAlert className="w-7 h-7 text-red-300" strokeWidth={1.75} />
            </div>
            <h1 className="text-white text-xl font-bold tracking-tight mt-5">Sign-in link problem</h1>
            <p className="text-white/70 text-sm mt-2 leading-relaxed">{errorMsg}</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center mt-6 px-5 py-3 rounded-xl bg-yellow-400 text-black font-bold text-sm hover:bg-yellow-300 transition-colors"
              data-testid="rest-magic-back-to-login"
            >
              Back to login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default RestaurantMagicLink;
